/**
 * Masonry column math for Explore — must use the **catalog body width** (after
 * sidebar / max-width), not the full window, or wide viewports still pick 4–5
 * columns while tiles are only ~120px wide.
 */

export const EXPLORE_MASONRY_CARD_GAP = 1;

/** Aim for tiles at least this wide when choosing column count. */
export const EXPLORE_GRID_TARGET_MIN_COLUMN_WIDTH = 168;

export const EXPLORE_GRID_MAX_COLUMNS = 5;

/** Phones stay at least 2 columns (matches prior Explore behavior). */
export const EXPLORE_GRID_MIN_COLUMNS = 2;

/** Single-column feed cap (~Instagram web feed width). */
export const EXPLORE_LIST_MAX_WIDTH = 400;

/** List feed uses a consistent square tile so row height stays predictable. */
export const EXPLORE_LIST_FEED_ASPECT = 1;

/** Clamp list tile aspect (width ÷ height) so tiles are not full-bleed panoramas or strips. */
export const EXPLORE_LIST_MIN_ASPECT = 0.8;
export const EXPLORE_LIST_MAX_ASPECT = 1.25;

/**
 * @param {number} bodyWidth — measured scroll area / masonry width (px)
 * @param {'grid'|'list'} viewMode
 * @returns {number}
 */
export function getExploreMasonryColumnCount(bodyWidth, viewMode) {
  if (viewMode === 'list') return 1;
  const w = typeof bodyWidth === 'number' && bodyWidth > 0 ? bodyWidth : 360;
  const eff = Math.max(280, w);
  const gap = EXPLORE_MASONRY_CARD_GAP;
  const unit = EXPLORE_GRID_TARGET_MIN_COLUMN_WIDTH + gap;
  const raw = Math.floor((eff + gap) / unit);
  return Math.max(EXPLORE_GRID_MIN_COLUMNS, Math.min(EXPLORE_GRID_MAX_COLUMNS, raw));
}

/**
 * @param {number} bodyWidth
 * @param {number} numColumns
 * @param {number} [gap]
 * @param {'grid'|'list'} [viewMode]
 * @returns {number}
 */
export function getExploreMasonryColumnWidth(bodyWidth, numColumns, gap = EXPLORE_MASONRY_CARD_GAP, viewMode = 'grid') {
  if (viewMode === 'list') {
    const w = typeof bodyWidth === 'number' && bodyWidth > 0 ? bodyWidth : 360;
    return Math.floor(Math.min(w, EXPLORE_LIST_MAX_WIDTH));
  }
  if (numColumns < 1) return Math.max(0, Math.floor(bodyWidth));
  const totalGaps = (numColumns - 1) * gap;
  const availableWidth = Math.max(0, bodyWidth - totalGaps);
  return Math.floor(availableWidth / numColumns);
}

/** Raw width÷height from explore item metadata. */
export function getExploreItemRawAspectRatio(item) {
  if (item?.metadata?.aspectRatio) return item.metadata.aspectRatio;
  if (item?.metadata?.width && item?.metadata?.height) {
    return item.metadata.width / item.metadata.height;
  }
  if (item?.mediaType === 'music' || item?.mediaType === '3d') return 1;
  return 0.75;
}

export function getExploreListAspectRatio(rawAspect) {
  const ar = typeof rawAspect === 'number' && rawAspect > 0 ? rawAspect : 1;
  return Math.min(EXPLORE_LIST_MAX_ASPECT, Math.max(EXPLORE_LIST_MIN_ASPECT, ar));
}

/**
 * @param {object} item
 * @param {'grid'|'list'} viewMode
 */
export function getExploreItemAspectRatio(item, viewMode) {
  if (viewMode === 'list') return EXPLORE_LIST_FEED_ASPECT;
  return getExploreItemRawAspectRatio(item);
}

/** Vertical space per list-feed row (square card + feed gutters). */
export function getExploreListFeedItemHeight(columnWidth, rowGap = 12) {
  const w = columnWidth > 0 ? columnWidth : EXPLORE_LIST_MAX_WIDTH;
  return Math.ceil(w / EXPLORE_LIST_FEED_ASPECT) + rowGap;
}
